// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Car, CheckCircle, XCircle, RefreshCw, ExternalLink, Pencil, Trash2, UserCheck } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { isGatewayPayment } from '@/lib/paymentGateway'
import { Alert, Button, Card, Badge, EmptyState, Skeleton, Modal, ConfirmDialog } from '@/components/ui'
import {
  VerifyPaymentModal,
  RejectPaymentModal,
  RetryVerificationModal,
} from '@/components/payments/VerificationActionModals'
import { IdentifyPayerModal } from '@/components/payments/IdentifyPayerModal'
import { SlipFieldChecklist, buildChecklistRows } from '@/components/payments/SlipFieldChecklist'
import { ML_MATCH_CHIP } from '@/components/payments/mlMatchChip'
import { SlipGateChips, FraudSuspectBanner } from '@/components/payments/SlipGateChips'
import { usePayment, useDeletePaymentFromList } from '@/hooks/usePayments'
import { useAuth } from '@/hooks/useAuth'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import { hiddenInSession } from '@/lib/sessionRedaction'
import { SupportSessionHidden } from '@/components/SupportSessionHidden'

type VerificationStatus = 'pending' | 'verifying' | 'verified' | 'rejected' | 'failed'

function getVerificationBadgeVariant(status: string): 'warning' | 'info' | 'success' | 'error' | 'ghost' {
  const variants: Record<VerificationStatus, 'warning' | 'info' | 'success' | 'error'> = {
    pending: 'warning',
    verifying: 'info',
    verified: 'success',
    rejected: 'error',
    failed: 'error',
  }
  return variants[status as VerificationStatus] ?? 'ghost'
}

export function PaymentDetailPage() {
  const { t, i18n } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const paymentId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const formatDate = useDateFormatter()

  const { data: payment, isLoading, isFetching } = usePayment(paymentId)

  const [showVerifyModal, setShowVerifyModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showRetryModal, setShowRetryModal] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [showDelete, setShowDelete] = useState(false)
  const [showIdentifyPayer, setShowIdentifyPayer] = useState(false)
  // Field keys the operator ticked in the manual slip check — feeds the
  // Verify modal's prefilled audit reason.
  const [checkedFields, setCheckedFields] = useState<string[]>([])
  const deletePayment = useDeletePaymentFromList()

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  // T149-074 (MON-19 C): identify_payer? is admin_or_higher? || accountant?,
  // the same gate as update?/verify?/reject?; staff never sees the action.
  const canIdentifyPayer = isAdminOrOwner || user?.role === 'accountant'

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <Skeleton className="h-48" />
        </Card>
      </div>
    )
  }

  if (!payment) {
    return (
      <Card>
        <EmptyState
          title={tc('errors.not_found')}
          action={<Button onClick={() => navigate('/payments')}>{tc('actions.back')}</Button>}
        />
      </Card>
    )
  }

  const meta = payment.slip_metadata ?? {}
  const verificationStatus = payment.verification_status
  // T149-074: hidden once the payment has a customer, since the action only ever
  // applies to a genuinely unidentified payment (Payment.unidentified: no
  // customer AND no invoice; a parking payment never surfaces it either).
  const canIdentifyThisPayment = canIdentifyPayer && !payment.customer
  // EP-161 D4: staff verify and reject a kiosk parking payment too
  // (PaymentPolicy#verify?/#reject?); Retry stays admin and owner.
  const parking = payment.parking ?? null
  const canDecide = isAdminOrOwner || (user?.role === 'staff' && parking !== null)
  // EP-143 T143-014: gate evidence + fraud signals from the gate ladder.
  const gateEvidence = meta.gate_evidence
  const fraudSuspect = meta.fraud_suspect
  const apiBase = import.meta.env.VITE_API_URL || ''
  const resolveImageUrl = (url: string) =>
    url.startsWith('http') ? url : `${apiBase}/api/images/${url}`
  // Parking payments (EP-105) carry the slip as a bare storage key in
  // slip_image_key with no slip_url/image_urls — same /api/images/:key render.
  const rawImageUrls = (payment.image_urls ?? []).length > 0
    ? payment.image_urls!
    : payment.slip_url
      ? [payment.slip_url]
      : payment.slip_image_key
        ? [payment.slip_image_key]
        : []
  const imageUrls = rawImageUrls.map(resolveImageUrl)
  const hasImages = imageUrls.length > 0
  // EP-160 S3: a FlowRent support session gets no slip image key at all,
  // only whether one exists, so the page says it is hidden.
  const slipHidden = !hasImages &&
    ['image_urls', 'slip_url', 'slip_image_key'].some((key) => hiddenInSession(payment, key))

  const rejectionReasons: string[] = Array.isArray(meta.rejection_reasons)
    ? meta.rejection_reasons
    : []

  // RDCW/ML structured readings carry account numbers as `{type, value}`
  // envelopes (e.g. {type:"BANKAC", value:"XXXXX2873X"}). The page renders
  // these as JSX children — an object is not a valid React child, so we
  // unwrap `.value` here. Plain string fallbacks still pass through.
  const accountToString = (v: unknown): string => {
    if (typeof v === 'string') return v
    if (v && typeof v === 'object' && 'value' in v && typeof (v as { value: unknown }).value === 'string') {
      return (v as { value: string }).value
    }
    return ''
  }
  const senderName = meta.sender_name ?? meta.sender ?? ''
  const senderAccount = accountToString(meta.sender_account) || accountToString(meta.sender_bank_account) || ''
  const receiverName = meta.receiver_name ?? meta.receiver ?? ''
  const receiverAccount = accountToString(meta.receiver_account) || accountToString(meta.receiver_bank_account) || ''
  const transferDate = meta.transfer_date ?? meta.date ?? ''
  const provider = meta.provider ?? ''
  const verifiedAmount = meta.amount ?? (verificationStatus === 'verified' ? payment.amount : undefined)

  // Per-adapter opinions from the structured readings bucket
  const rdcwReading = meta.readings?.rdcw
  const mlReading = meta.readings?.ml

  // Record-only ML match triage — never drives verification_status; it tells
  // the operator whether the slip looks like it matches this payment.
  const mlMatch = meta.ml_match

  // Prefill the Verify modal's audit reason from the ticked manual-check rows.
  const checklistRows = mlReading ? buildChecklistRows(payment, mlReading, t, formatDate) : []
  const checkedLabels = checklistRows
    .filter((row) => checkedFields.includes(row.key))
    .map((row) => row.label)
  const verifyInitialReason = checkedLabels.length > 0
    ? t('verification.fields_checked_reason', { fields: checkedLabels.join(', ') })
    : undefined

  // Provider chips: show one chip per opinion that exists.
  // When both opinions are present both chips are rendered (D4), except that
  // the ml_match triage chip supersedes the generic "ML reading" chip — one
  // ML chip, the most informative one.
  const providerChips: Array<{ key: string; label: string }> = []
  if (rdcwReading || mlReading) {
    if (rdcwReading) providerChips.push({ key: 'rdcw', label: t('verification.provider_label_rdcw') })
    if (mlReading && !mlMatch) providerChips.push({ key: 'ml', label: t('verification.provider_label_ml') })
  } else if (provider) {
    if (provider === 'ml') {
      providerChips.push({ key: 'ml', label: t('verification.provider_label_ml') })
    } else if (provider === 'rdcw') {
      providerChips.push({ key: 'rdcw', label: t('verification.provider_label_rdcw') })
    } else {
      providerChips.push({ key: provider, label: t('verification.provider_label_other', { provider }) })
    }
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/payments' },
            { label: `#${payment.id}` },
          ]}
        />

        <PageHeader
          title={`${t('labels.date')}: ${formatDate(payment.paid_at) || '-'}`}
          description={
            payment.invoice_number
              ? `${t('labels.invoice')}: ${payment.invoice_number}`
              : undefined
          }
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — Edit is the primary for admin/owner; staff sees no
          // header actions (Verify/Reject/Retry stay in the body override card).
          primaryAction={{
            key: 'edit',
            label: t('actions.edit_payment'),
            icon: Pencil,
            onClick: () => navigate(`/payments/${paymentId}/edit`),
            hidden: !isAdminOrOwner,
          }}
          overflowActions={[
            {
              key: 'identify-payer',
              label: t('identify_payer.action'),
              icon: UserCheck,
              onClick: () => setShowIdentifyPayer(true),
              hidden: !canIdentifyThisPayment,
            },
            {
              key: 'delete',
              label: t('actions.delete_payment'),
              icon: Trash2,
              onClick: () => setShowDelete(true),
              destructive: true,
              hidden: !isAdminOrOwner,
            },
          ]}
        />

        {/* EP-143 T143-014: Fraud-suspect banner — shown when the gate ladder
            flagged this payment as a fraud signal. Renders above all cards. */}
        <FraudSuspectBanner fraudSuspect={fraudSuspect} />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Payment Info */}
          <Card className="lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4">{t('title')}</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.invoice')}</dt>
                <dd className="font-medium break-words">
                  {payment.invoice_number ? (
                    <Link to={`/invoices/${payment.invoice_id}`} className="link link-primary inline-flex items-center gap-1">
                      {payment.invoice_number}
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.amount')}</dt>
                <dd className="font-medium text-success">{formatBaht(payment.amount, { locale: 'en', decimals: 2 })}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.method')}</dt>
                <dd className="font-medium">
                  {t(`methods.${payment.payment_method}`)}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.date')}</dt>
                <dd className="font-medium">{formatDate(payment.paid_at) || '-'}</dd>
              </div>
              {payment.reference && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.reference')}</dt>
                  <dd className="font-medium text-base-content/70 break-words">{payment.reference}</dd>
                </div>
              )}
              {payment.customer && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.customer')}</dt>
                  <dd className="font-medium break-words">
                    <Link to={`/customers/${payment.customer.id}`} className="link link-primary">
                      {payment.customer.name}
                    </Link>
                  </dd>
                </div>
              )}
              {payment.unit && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.unit')}</dt>
                  <dd className="font-medium break-words">{payment.unit.name}</dd>
                </div>
              )}
            </dl>

            {/* Slip Images */}
            {hasImages && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-base-content/70 mb-2">
                  {t('verification.slip_images')}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {imageUrls.map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      className="block border border-base-300 rounded-lg overflow-hidden hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary"
                      onClick={() => setLightboxUrl(url)}
                      aria-label={t('verification.slip_image_alt', { count: i + 1 })}
                    >
                      <img
                        src={url}
                        alt={t('verification.slip_image_alt', { count: i + 1 })}
                        className="h-40 w-auto object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {slipHidden && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-base-content/70 mb-2">
                  {t('verification.slip_images')}
                </h3>
                <SupportSessionHidden testId="payment-slip-hidden" />
              </div>
            )}
          </Card>

          {/* Verification Info */}
          <div className="space-y-4">
            {/* Admin Override — kept above the ML reading detail so the
                operator actions don't sink below a long field list */}
            {/* EP-161 §2.4: the car this kiosk payment is for, with a link
                to its row on the property's parking sessions page. */}
            {parking && (
              <Card data-testid="payment-parking-block">
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <Car className="w-4 h-4" aria-hidden />
                  {t('detail.parking_block.title')}
                </h2>
                <dl className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <dt className="text-sm text-base-content/60">{t('detail.parking_block.plate')}</dt>
                    <dd className="font-medium break-words">{parking.license_plate}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-sm text-base-content/60">{t('detail.parking_block.zone')}</dt>
                    <dd className="font-medium break-words">{parking.zone_name || '-'}</dd>
                  </div>
                </dl>
                <Link
                  to={`/properties/${parking.property_id}/parking/sessions?session=${parking.session_id}`}
                  className="link link-primary inline-flex items-center gap-1 mt-3 text-sm"
                >
                  {t('detail.parking_block.open_sessions')}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </Card>
            )}

            {canDecide && (
              <Card>
                <h2 className="text-base font-semibold mb-3">{t('verification.override_section')}</h2>
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    leftIcon={<CheckCircle className="w-4 h-4" />}
                    onClick={() => setShowVerifyModal(true)}
                    className="text-success border-success hover:bg-success hover:text-success-content"
                  >
                    {t('actions.verify')}
                  </Button>
                  <Button
                    variant="outline"
                    leftIcon={<XCircle className="w-4 h-4" />}
                    onClick={() => setShowRejectModal(true)}
                    className="text-error border-error hover:bg-error hover:text-error-content"
                  >
                    {t('actions.reject')}
                  </Button>
                  {isAdminOrOwner && hasImages && (
                    <Button
                      variant="ghost"
                      leftIcon={<RefreshCw className="w-4 h-4" />}
                      onClick={() => setShowRetryModal(true)}
                    >
                      {t('actions.retry_verification')}
                    </Button>
                  )}
                </div>
              </Card>
            )}

            <Card>
              <h2 className="text-lg font-semibold mb-4">{t('verification.title')}</h2>

              {!verificationStatus && isGatewayPayment(payment) ? (
                <div className="space-y-3">
                  <p className="text-sm text-success font-medium">
                    {t('verification.gateway_confirmed', { provider: 'Stripe' })}
                  </p>
                  {/* EP-162 14.1: FlowRent does not refund through Stripe. */}
                  <Alert variant="info" data-testid="payment-stripe-refund-note">
                    {t('verification.stripe_refund_note')}
                  </Alert>
                </div>
              ) : !verificationStatus && !isGatewayPayment(payment) ? (
                <p className="text-sm text-base-content/60">{t('verification.manual_recorded')}</p>
              ) : (
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-base-content/60">{t('verification.status')}</dt>
                    <dd className="mt-1">
                      <Badge variant={getVerificationBadgeVariant(verificationStatus ?? '')}>
                        {t(`verification.statuses.${verificationStatus}`)}
                      </Badge>
                    </dd>
                  </div>
                  {(providerChips.length > 0 || mlMatch) && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.provider')}</dt>
                      <dd className="mt-1 flex flex-wrap gap-1">
                        {providerChips.map((chip) => (
                          <Badge key={chip.key} variant="ghost">
                            {chip.label}
                          </Badge>
                        ))}
                        {mlMatch && (
                          <Badge variant={ML_MATCH_CHIP[mlMatch.outcome].variant}>
                            {t(ML_MATCH_CHIP[mlMatch.outcome].labelKey)}
                          </Badge>
                        )}
                      </dd>
                      {mlMatch && mlMatch.reasons.length > 0 && (
                        <ul className="mt-1 list-disc list-inside space-y-0.5">
                          {mlMatch.reasons.map((reason) => (
                            <li key={reason} className="text-xs text-base-content/60">
                              {t(`verification.reasons.${reason}`)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {/* EP-143 T143-014: Gate-ladder chips (G0–G8 + P9).
                      Absent on old readings — backward compat: nothing renders. */}
                  {gateEvidence && (
                    <SlipGateChips gateEvidence={gateEvidence} />
                  )}

                  {payment.trans_ref && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.trans_ref')}</dt>
                      <dd className="font-medium font-mono text-sm break-all">{payment.trans_ref}</dd>
                    </div>
                  )}
                  {verifiedAmount !== undefined && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.amount')}</dt>
                      <dd className="font-medium">{formatBaht(verifiedAmount, { locale: 'en', decimals: 2 })}</dd>
                    </div>
                  )}
                  {senderName && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.sender')}</dt>
                      <dd className="font-medium">{senderName}</dd>
                    </div>
                  )}
                  {senderAccount && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.sender_account')}</dt>
                      <dd className="font-medium font-mono text-sm">{senderAccount}</dd>
                    </div>
                  )}
                  {receiverName && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.receiver')}</dt>
                      <dd className="font-medium">{receiverName}</dd>
                    </div>
                  )}
                  {receiverAccount && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.receiver_account')}</dt>
                      <dd className="font-medium font-mono text-sm">{receiverAccount}</dd>
                    </div>
                  )}
                  {transferDate && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.transfer_date')}</dt>
                      <dd className="font-medium">{transferDate}</dd>
                    </div>
                  )}
                  {rejectionReasons.length > 0 && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('verification.rejection_reasons')}</dt>
                      <dd className="mt-1">
                        <ul className="list-disc list-inside space-y-1">
                          {rejectionReasons.map((reason, i) => (
                            // rejection_reasons can hold a manual reject's free-typed operator
                            // text (Api::PaymentsController#reject `params[:reason]`), not just
                            // the fixed RDCW/ML codes — branch on i18n.exists() so free text
                            // renders as-is instead of a silent fallback string.
                            <li key={i} className="text-sm text-error">
                              {i18n.exists(`payments:verification.reasons.${reason}`)
                                ? t(`verification.reasons.${reason}`)
                                : reason}
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )}

                  {/* ML per-field confidence detail — only shown when readings.ml is present */}
                  {mlReading && (
                    <div className="pt-2 border-t border-base-200">
                      <dt className="text-sm font-medium text-base-content/80 mb-2">
                        {t('verification.ml_reading_detail')}
                      </dt>
                      <dd>
                        <dl className="space-y-2">
                          {/* Compare rows (amount/datetime, check buttons prefill the
                              Verify modal's audit reason) + correction inputs for the
                              remaining fields (bank incl. classifier pick, sender,
                              receiver, trans_ref, ref1) saved as training labels. */}
                          <SlipFieldChecklist
                            payment={payment}
                            mlReading={mlReading}
                            checkedFields={checkedFields}
                            onToggleField={
                              isAdminOrOwner
                                ? (fieldKey) =>
                                    setCheckedFields((prev) =>
                                      prev.includes(fieldKey)
                                        ? prev.filter((k) => k !== fieldKey)
                                        : [...prev, fieldKey]
                                    )
                                : undefined
                            }
                          />
                          {mlReading.reading_confidence !== undefined && (
                            <div className="flex justify-between text-sm font-medium border-t border-base-200 pt-2 mt-1">
                              <span className="text-base-content/60">{t('verification.reading_confidence')}</span>
                              <span className="font-mono">{(mlReading.reading_confidence * 100).toFixed(1)}%</span>
                            </div>
                          )}
                          {mlReading.model_versions && (
                            <div className="pt-1">
                              <span className="text-xs text-base-content/50">{t('verification.model_versions')}: </span>
                              <span className="text-xs font-mono text-base-content/50">
                                {[
                                  mlReading.model_versions.slip_bank_classifier
                                    ? `${t('verification.model_bank_classifier')} v${mlReading.model_versions.slip_bank_classifier}`
                                    : null,
                                  mlReading.model_versions.slip_field_ocr
                                    ? `${t('verification.model_field_ocr')} v${mlReading.model_versions.slip_field_ocr}`
                                    : null,
                                ].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                          {/* ref2 from QR — ref1 lives in the checklist above;
                              omitted (not rendered as '—') when absent */}
                          {mlReading.qr?.ref2 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-base-content/60">Ref2</span>
                              <span className="font-mono text-xs">{mlReading.qr.ref2}</span>
                            </div>
                          )}
                        </dl>
                      </dd>
                    </div>
                  )}
                </dl>
              )}
            </Card>

          </div>
        </div>
      </div>

      {/* Manual verification actions (shared with PaymentListPage) */}
      <VerifyPaymentModal
        paymentId={paymentId}
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        initialReason={verifyInitialReason}
      />
      <RejectPaymentModal
        paymentId={paymentId}
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
      />
      <RetryVerificationModal
        paymentId={paymentId}
        isOpen={showRetryModal}
        onClose={() => setShowRetryModal(false)}
      />
      {/* Mounted only while open, unlike the verify/reject/retry modals above:
          those call mutation hooks only (no network until submit), but this
          one's customer/invoice pickers are QUERY hooks that fetch on mount. */}
      {showIdentifyPayer && (
        <IdentifyPayerModal
          paymentId={paymentId}
          isOpen
          onClose={() => setShowIdentifyPayer(false)}
        />
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <Modal
          isOpen={!!lightboxUrl}
          onClose={() => setLightboxUrl(null)}
          size="3xl"
        >
          <div className="flex items-center justify-center">
            <img
              src={lightboxUrl}
              alt={t('verification.slip_image_lightbox_alt')}
              className="max-h-[70vh] max-w-full object-contain rounded"
            />
          </div>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={async () => {
          await deletePayment.mutateAsync({ id: paymentId, invoiceId: payment.invoice_id })
          setShowDelete(false)
          navigate('/payments')
        }}
        title={t('actions.delete_payment')}
        message={t('confirm.delete')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deletePayment.isPending}
      />
    </>
  )
}
